import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { dynamics } from "@/mocks/dynamics";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const quickLinks = [
  { icon: "ri-film-line", label: "全部作品", sub: `${works.length} 部影视档案`, to: "/works" },
  { icon: "ri-newspaper-line", label: "影视资讯", sub: "评论 · 报道 · 动态", to: "/news" },
  { icon: "ri-user-star-line", label: "演员动态", sub: "最新行程与项目", to: "/news?category=演员动态" },
  { icon: "ri-information-line", label: "关于吴梦梦", sub: "品牌定位与联络", to: "/about" },
];

const latestWorks = [...works]
  .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
  .slice(0, 4);

const latestNews = [...news].sort((a, b) => b.date.localeCompare(a.date));
const homeNews = latestNews.slice(0, 5);

const hotWorks = [...works].sort((a, b) => b.heat - a.heat).slice(0, 5);

function SectionHeader({
  eyebrow,
  title,
  description,
  to,
  linkLabel,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  to?: string;
  linkLabel?: string;
}) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5 md:mb-7">
      <div className="min-w-0">
        <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
          {eyebrow}
        </span>
        <h2 className="mt-2 text-xl md:text-[26px] font-bold leading-tight">{title}</h2>
        {description && <p className="mt-2 text-xs md:text-sm text-foreground-500">{description}</p>}
      </div>
      {to && linkLabel && (
        <Link
          to={to}
          className="shrink-0 inline-flex items-center gap-1 text-xs md:text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap"
        >
          {linkLabel}
          <i className="ri-arrow-right-line text-sm leading-none" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  useDocumentMeta(
    "吴梦梦影视官网 - 最新电影电视剧资讯与作品展示",
    "吴梦梦影视官网提供丰富的电影、电视剧资讯与作品展示，涵盖演员动态、影视评论和最新项目，让影迷全面了解吴梦梦的影视之路。",
    "吴梦梦,影视作品,电影,电视剧,演员资讯,影视动态,吴梦梦电影,吴梦梦电视剧",
  );

  return (
    <div>
      <HeroCarousel />

      <div className="container-content relative z-10 -mt-7 md:-mt-9">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-3">
          {quickLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="group flex items-center gap-3 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
            >
              <span className="shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 group-hover:bg-accent-100 group-hover:text-accent-700 transition-colors">
                <i className={`${link.icon} text-lg leading-none`} />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] md:text-sm font-semibold text-foreground-950 truncate">
                  {link.label}
                </span>
                <span className="block mt-0.5 text-[10px] md:text-[11px] text-foreground-500 truncate">
                  {link.sub}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      <section className="container-content py-10 md:py-14">
        <SectionHeader
          eyebrow="Latest Works"
          title="最新影视作品"
          description="按上映时间整理的最新上线与待映项目"
          to="/works"
          linkLabel="查看全部"
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {latestWorks.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 border-y border-background-200">
        <div className="container-content py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-9 lg:gap-10">
            <div>
              <SectionHeader
                eyebrow="Latest News"
                title="最新影视资讯"
                description="按发布时间倒序排列的影视评论、行业新闻与活动报道"
                to="/news"
                linkLabel="全部资讯"
              />
              <div className="flex flex-col divide-y divide-background-200 bg-background-50 rounded-lg border border-background-200 px-4 md:px-5">
                {homeNews.map((item) => (
                  <div key={item.id} className="py-4 md:py-5">
                    <NewsCard news={item} />
                  </div>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3.5 border-b border-background-200">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <i className="ri-flashlight-line text-accent-500 leading-none" />
                    演员动态快讯
                  </h2>
                  <span className="text-[10px] font-label tracking-widest text-foreground-400">LIVE</span>
                </div>
                <ul className="divide-y divide-background-200">
                  {dynamics.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/${item.newsId}`}
                        className="group flex items-start gap-3 px-4 py-3 hover:bg-background-100 transition-colors"
                      >
                        <span className="shrink-0 mt-0.5 text-[11px] font-label font-bold text-accent-600 w-9">
                          {item.date}
                        </span>
                        <span className="flex-1 text-[13px] text-foreground-700 leading-snug group-hover:text-foreground-950">
                          {item.text}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/news?category=演员动态"
                  className="flex items-center justify-center gap-1 h-10 text-xs text-foreground-600 hover:text-accent-600 border-t border-background-200 transition-colors"
                >
                  查看更多动态
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <div className="px-4 py-3.5 border-b border-background-200">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <i className="ri-fire-line text-accent-500 leading-none" />
                    热度作品榜
                  </h2>
                </div>
                <ul className="divide-y divide-background-200">
                  {hotWorks.map((work, index) => (
                    <li key={work.id}>
                      <Link to={`/works/${work.id}`} className="group flex items-center gap-3 px-4 py-3 hover:bg-background-100 transition-colors">
                        <span
                          className={`shrink-0 w-5 text-center font-label font-extrabold text-sm ${
                            index < 3 ? "text-accent-600" : "text-foreground-400"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="w-10 h-[54px] shrink-0 rounded overflow-hidden bg-background-200">
                          <img
                            src={work.cover}
                            alt={`${work.title} 封面`}
                            title={`${work.title} 吴梦梦影视作品`}
                            className="w-full h-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-medium text-foreground-950 truncate group-hover:text-primary-600">
                            {work.title}
                          </span>
                          <span className="block mt-1 text-[11px] text-foreground-500 font-label">
                            {work.type} · {work.year}
                          </span>
                        </span>
                        <span className="shrink-0 text-[11px] font-label text-foreground-400 flex items-center gap-0.5">
                          <i className="ri-star-fill text-accent-400 text-[10px] leading-none" />
                          {work.rating.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="container-content py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="order-2 lg:order-1">
            <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
              About the Brand
            </span>
            <h2 className="mt-3 text-2xl md:text-[32px] font-bold leading-snug">
              一个把角色留在银幕上的演员品牌
            </h2>
            <p className="mt-4 text-sm md:text-[15px] text-foreground-600 leading-relaxed">
              吴梦梦影视官网以作品档案为核心，系统整理她自 2022 年以来的电影与电视剧创作，同时收录影评、行业报道与演员动态。
              我们相信，一个演员的价值不只在于曝光度，更在于她愿意为每个角色花多少时间。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3 md:gap-4">
              {[
                { value: `${works.length}`, label: "影视作品" },
                { value: `${news.length}`, label: "资讯报道" },
                { value: "50", label: "收藏上限" },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-3.5 rounded-lg bg-background-100 border border-background-200">
                  <div className="font-label text-xl md:text-2xl font-extrabold text-primary-600">{stat.value}</div>
                  <div className="mt-1 text-[11px] text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-7 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              了解吴梦梦
              <i className="ri-arrow-right-line text-base leading-none" />
            </Link>
          </div>

          <div className="order-1 lg:order-2 w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20portrait%20scene%20of%20an%20elegant%20East%20Asian%20actress%20in%20a%20minimal%20dark%20studio%2C%20deep%20navy%20blue%20backdrop%20with%20a%20single%20warm%20orange%20light%20beam%20crossing%20the%20frame%2C%20soft%20film%20grain%2C%20refined%20cinematic%20mood%2C%20no%20text%2C%20no%20watermark&width=1000&height=750&seq=home-about&orientation=landscape"
              alt="吴梦梦品牌形象展示"
              title="吴梦梦 影视品牌形象"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>
    </div>
  );
}